import { useCallback, useEffect, useState } from "react";
import { Download } from "lucide-react";
import api, { BACKEND_URL, getToken } from "../lib/api";
import { extractError } from "../lib/formatError";

const TYPES = ["", "deposit", "withdrawal_hold", "withdrawal_paid", "withdrawal_refund", "bet_debit", "bet_win", "bet_loss", "refund", "demo_topup", "admin_adjust"];
const MODES = ["", "demo", "real"];

export default function AdminTransactions() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ from: "", to: "", user: "", type: "", mode: "", amount_min: "", amount_max: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const buildQuery = () => {
    const p = new URLSearchParams({ limit: "100" });
    for (const [k, v] of Object.entries(filters)) {
      if (v !== "" && v !== null && v !== undefined) p.set(k, v);
    }
    return p;
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/admin/transactions?${buildQuery()}`);
      setItems(data.items); setTotal(data.total);
    } catch (e) { setError(extractError(e)); } finally { setLoading(false); }
    // Initial mount fetch only; subsequent loads are triggered explicitly by
    // the filter form's onSubmit. Excluding `buildQuery` (closure over
    // `filters`) is intentional — including it would refetch on every
    // keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const download = async () => {
    // Force fetch with auth header, then trigger a download blob
    const r = await fetch(`${BACKEND_URL}/api/admin/transactions/export.csv?${buildQuery()}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!r.ok) { setError(`Export failed (${r.status})`); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `transactions_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-transactions">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-[color:var(--muted)] mb-2">Transactions</div>
          <h1 className="font-display italic text-4xl text-[color:var(--ink)]">Ledger ({total})</h1>
        </div>
        <button onClick={download} className="px-4 py-2 border border-[color:var(--gold)] text-[color:var(--gold)] text-[10px] tracking-widest uppercase flex items-center gap-2 hover:bg-[color:var(--card)]" data-testid="export-csv-btn">
          <Download className="w-3 h-3" />Export CSV
        </button>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <F label="From (ISO)" value={filters.from} onChange={(v) => setFilters({ ...filters, from: v })} placeholder="2026-01-01" testid="filter-from" />
        <F label="To (ISO)" value={filters.to} onChange={(v) => setFilters({ ...filters, to: v })} placeholder="2026-12-31" testid="filter-to" />
        <F label="User (id or email)" value={filters.user} onChange={(v) => setFilters({ ...filters, user: v })} testid="filter-user" />
        <FSelect label="Type" value={filters.type} onChange={(v) => setFilters({ ...filters, type: v })} options={TYPES} testid="filter-type" />
        <FSelect label="Mode" value={filters.mode} onChange={(v) => setFilters({ ...filters, mode: v })} options={MODES} testid="filter-mode" />
        <F label="Amount min" value={filters.amount_min} onChange={(v) => setFilters({ ...filters, amount_min: v })} type="number" testid="filter-min" />
        <F label="Amount max" value={filters.amount_max} onChange={(v) => setFilters({ ...filters, amount_max: v })} type="number" testid="filter-max" />
        <button type="submit" className="self-end px-3 py-2 border border-[color:var(--line)] text-[10px] tracking-widest uppercase text-[color:var(--muted)] hover:text-[color:var(--gold)] hover:border-[color:var(--gold)]" data-testid="filter-apply">Apply</button>
      </form>

      {error && <div className="text-xs text-[color:var(--red)] border border-[color:var(--red)] px-3 py-2 mb-3">{error}</div>}
      {loading ? <div className="text-[color:var(--muted)] text-sm">Loading…</div>
        : items.length === 0 ? <div className="text-[color:var(--muted)] text-sm italic">No transactions.</div>
        : (
          <div className="border overflow-x-auto" style={{ borderColor: "var(--line)" }}>
            <table className="w-full text-xs">
              <thead className="border-b text-[10px] tracking-widest uppercase text-[color:var(--muted)]" style={{ borderColor: "var(--line)" }}>
                <tr>
                  <th className="text-left px-3 py-2 font-normal">Date</th>
                  <th className="text-left px-3 py-2 font-normal">User</th>
                  <th className="text-left px-3 py-2 font-normal">Type</th>
                  <th className="text-left px-3 py-2 font-normal">Mode</th>
                  <th className="text-left px-3 py-2 font-normal">Amount</th>
                  <th className="text-left px-3 py-2 font-normal">Balance after</th>
                  <th className="text-left px-3 py-2 font-normal">Note</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => (
                  <tr key={t.id} className="border-b hover:bg-[color:var(--card)]" style={{ borderColor: "var(--line)" }} data-testid={`txn-${t.id}`}>
                    <td className="px-3 py-2 text-[color:var(--muted)] text-[10px]">{new Date(t.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2 text-[color:var(--ink)]">{t.user_email || t.user_id.slice(0, 8)}</td>
                    <td className="px-3 py-2 uppercase tracking-widest text-[10px]">{t.type}</td>
                    <td className="px-3 py-2 uppercase tracking-widest text-[10px]">{t.mode}</td>
                    <td className={`px-3 py-2 font-mono ${t.amount < 0 ? "text-[color:var(--red)]" : "text-[color:var(--gold)]"}`}>${t.amount.toFixed(2)}</td>
                    <td className="px-3 py-2 font-mono text-[10px]">${t.balance_after.toFixed(2)}</td>
                    <td className="px-3 py-2 text-[10px] text-[color:var(--muted)] break-all max-w-[240px]">{t.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

function F({ label, value, onChange, placeholder = "", type = "text", testid }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-widest uppercase text-[color:var(--muted)] mb-1">{label}</div>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="bet-input text-xs" data-testid={testid} />
    </label>
  );
}
function FSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-widest uppercase text-[color:var(--muted)] mb-1">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="bet-input text-xs" data-testid={testid}>
        {options.map((o) => <option key={o} value={o}>{o || "all"}</option>)}
      </select>
    </label>
  );
}
